import { useTranslation } from '@gem/modules/i18n';

import {
  categoryValidation,
  channelAliasCreateValidation,
  channelAliasEditValidation,
  channelNameValidation,
} from '../fields/fields';
import type { IFormFieldValidation, IValidateParams, ValidationResult } from '../hooks';
import { useAsyncValidation, useSyncValidation } from '../hooks';
import { ValidationErrorType } from '../validation.types';

export interface IChannelAliasAsyncDeps {
  channelAliasAsyncValidation: (params: IValidateParams) => Promise<string | null>;
}

export interface IChannelValidations {
  channelName: IFormFieldValidation;
  channelCategory: IFormFieldValidation;
  channelAliasCreate: IFormFieldValidation;
  channelAliasEdit: IFormFieldValidation;
}

export const useSharedChannelValidations = (deps: IChannelAliasAsyncDeps): IChannelValidations => {
  const { t } = useTranslation();

  const channelNameErrorToText = {
    [ValidationErrorType.Empty]: ' ',
    [ValidationErrorType.TooShort]: t('username_min_length_error'),
  };

  const channelCategoryErrorToText = {
    [ValidationErrorType.Empty]: ' ',
  };

  const channelAliasErrorToText = {
    [ValidationErrorType.Empty]: ' ',
    [ValidationErrorType.TooShort]: t('username_min_length_error'),
    [ValidationErrorType.IncorrectSymbols]: t('channel_alias_allowed_symbols_error'),
    [ValidationErrorType.AlreadyExists]: t('channel_link_is_not_available'),
  };

  const channelNameValidator = useSyncValidation();
  const channelCategoryValidator = useSyncValidation();
  const channelAliasValidator = useAsyncValidation();

  return {
    channelName: {
      validate: (value, _formData, meta): ValidationResult => {
        return channelNameValidator({
          value,
          meta: meta!,
          validate: channelNameValidation,
          errorToText: channelNameErrorToText,
        });
      },
    },
    channelCategory: {
      validate: (value, _formData, meta): ValidationResult => {
        return channelCategoryValidator({
          value,
          meta: meta!,
          validate: categoryValidation,
          errorToText: channelCategoryErrorToText,
        });
      },
    },
    channelAliasCreate: {
      validate: (value, formData, meta): ValidationResult => {
        return channelAliasValidator({
          value,
          values: formData,
          meta: meta!,
          validate: channelAliasCreateValidation,
          asyncValidate: deps.channelAliasAsyncValidation,
          errorToText: channelAliasErrorToText,
        });
      },
    },
    channelAliasEdit: {
      validate: (value, formData, meta): ValidationResult => {
        return channelAliasValidator({
          value,
          values: formData,
          meta: meta!,
          validate: channelAliasEditValidation,
          asyncValidate: deps.channelAliasAsyncValidation,
          errorToText: channelAliasErrorToText,
        });
      },
    },
  };
};
